import * as THREE from 'three';
import type { Viewport } from './viewport.ts';
import { state } from '../state.ts';

export interface RenderLoop {
  start: () => void;
  stop: () => void;
  /** Render a single frame on demand (used while scrubbing / exporting). */
  renderOnce: () => void;
}

/**
 * requestAnimationFrame loop. Advances the AnimationMixer only while playing,
 * keeps the camera alive every frame, and reports playback frame changes via
 * onFrame so the timeline UI can follow along.
 *
 * `updateCamera` replaces the unconditional OrbitControls update: with damping
 * on, controls.update() rewrites the camera every frame and would stomp any
 * programmatic (animated-camera) pose, so the camera director decides whether
 * to run the controls or drive the camera itself.
 *
 * Scrubbing and export deliberately stop this loop / pause the clock and drive
 * time through mixer.setTime instead, so frame stepping stays deterministic.
 */
export function createRenderLoop(
  vp: Viewport,
  onFrame: (frame: number) => void,
  onTick?: () => void,
  updateCamera?: () => void
): RenderLoop {
  const driveCamera = updateCamera ?? (() => vp.controls.update());
  const clock = new THREE.Clock();
  let rafId = 0;
  let running = false;

  function frame(): void {
    rafId = requestAnimationFrame(frame);
    const delta = clock.getDelta();

    if (state.playing && state.mixer && state.totalFrames > 0) {
      for (const m of state.mixers) m.update(delta);
      // Loop playback within the marked [start, end] range.
      const startSec = state.startFrame / state.fps;
      const endSec = state.endFrame / state.fps;
      if (state.mixer.time > endSec) {
        for (const m of state.mixers) m.setTime(startSec);
      }
      const f = Math.round(state.mixer.time * state.fps);
      if (f !== state.currentFrame) {
        state.currentFrame = f;
        onFrame(f);
      }
    }

    driveCamera();
    onTick?.();
    vp.render();
  }

  return {
    start() {
      if (running) return;
      running = true;
      clock.getDelta(); // discard the accumulated delta from idle time
      frame();
    },
    stop() {
      running = false;
      cancelAnimationFrame(rafId);
    },
    renderOnce() {
      driveCamera();
      onTick?.();
      vp.render();
    },
  };
}
